<template>
    <div class="mt-8 lg:mt-0">
        <hr class="border-t border-gray-300 my-8 lg:hidden" />

        <div id="popular">
            <h3 class="base_bold">
                {{ $t('news_popular_posts') }}
            </h3>

            <Loading class="mt-4" :loading="loading">
                <div class="-mx-4 gap-2 flex flex-col">
                    <div v-for="item in $store.getters['posts/getByKey']('popular')?.data" :key="item.id">
                        <TradingRoomPostItem :key="item.id" layout="small_image" :contentstream="item" />
                    </div>
                </div>

                <div class="flex justify-end mt-4">
                    <Pagination :limit="-1" :data="$store.getters['posts/getByKey']('popular')" @pagination-change-page="paginate" />
                </div>
            </Loading>
        </div>

        <hr class="border-t border-gray-300 my-8" />

        <div id="latest-videos-hkcm">
            <h3 class="base_bold">
                {{ $t('news_latest_videos') }}
            </h3>
            <div v-if="youtube">
                <VideoList
                    class="mt-4"
                    :parameters="{
                        'sort[by]': 'created_at',
                        'sort[order]': 'desc',
                        playlist_id: region !== 'de' ? 'UUvLnTGJQthqju38QPtaD64Q' : 'UU3AdN1bEmEonuSwXNS8LixQ',
                        key: region !== 'de' ? 'UUvLnTGJQthqju38QPtaD64Q' : 'UU3AdN1bEmEonuSwXNS8LixQ',
                        limit: 3,
                        type: ['youtube']
                    }"
                />
            </div>
            <div v-else>
                <CookieReset />
            </div>

            <hr v-if="region === 'de'" class="border-t border-gray-300 my-8" />

            <div v-if="region === 'de'" id="latest-videos-phantom">
                <h3 class="base_bold">
                    {{ $t('news_latest_videos_phantom') }}
                </h3>
                <div v-if="youtube">
                    <VideoList
                        class="mt-4"
                        :parameters="{
                            'sort[by]': 'created_at',
                            'sort[order]': 'desc',
                            playlist_id: 'PLsIDvtZfmyZQe9Nmi3sVxwhzn8151Cmpd',
                            key: 'PLsIDvtZfmyZQe9Nmi3sVxwhzn8151Cmpd',
                            limit: 3,
                            type: ['youtube']
                        }"
                    />
                </div>
                <div v-else>
                    <CookieReset />
                </div>
            </div>

            <hr class="border-t border-gray-300 my-8" />

            <div id="top-performer">
                <h3 class="base_bold">
                    {{ $t('news_global_markets') }}
                </h3>
                <div v-if="tradingView" class="relative">
                    <a
                        href="https://www.tradingview.com/?aff_id=23069&source=NEWS"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="absolute top-2 right-2 text-[10px] bg-white/80 backdrop-blur-sm px-2 py-1 rounded-md shadow-sm hover:bg-white transition"
                    >
                        Try on TradingView →
                    </a>

                    <MarketOverview class="block mt-4 w-auto max-w-full" :options="marketOverviewOptions" />
                    <p class="text-center text-gray-500 text-sm mt-2">
                        Powered by
                        <a href="https://www.tradingview.com/?aff_id=23069&source=NEWS" target="_blank" rel="noopener noreferrer" class="underline hover:text-blue-600 transition">
                            TradingView
                        </a>
                    </p>
                </div>
                <div v-else>
                    <CookieReset />
                </div>
            </div>

            <hr class="border-t border-gray-300 my-8" />

            <div id="social">
                <h3 class="base_bold">
                    {{ $t('news_social') }}
                </h3>
                <div class="mt-4 block">
                    <TwitterFeed v-if="twitter" />
                    <div v-else>
                        <CookieReset />
                    </div>
                </div>
            </div>

            <hr class="border-t border-gray-300 my-8" />

            <div id="our-products">
                <h3 class="base_bold">
                    {{ $t('news_our_products') }}
                </h3>
                <div class="grid gap-4 mt-4">
                    <ProductCategorySingle v-for="category in $store.getters['product_categories/get']()" :key="category.id" :data="category" class="bg-white" />
                </div>
                <div class="flex justify-end mt-4">
                    <router-link to="/produkte">
                        <div class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center mt-2 cursor-pointer">
                            <span>{{ $t('news_all_products') }}</span>
                            <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current" />
                        </div>
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import i18next from 'i18next';
import _ from 'lodash';
import { MarketOverview } from 'vue-tradingview-widgets';
import { useCcm19Global } from '../../useCcm19Global';
import TwitterFeed from '../social_media/TwitterFeed.vue';
import CookieReset from './../CookieReset.vue';

export default {
    components: {
        MarketOverview,
        TwitterFeed,
        CookieReset
    },
    setup() {
        const { onReady, youtube, tradingView, twitter } = useCcm19Global();
        onReady(() => {});

        return { youtube, tradingView, twitter };
    },
    data: () => ({
        loading: false
    }),
    computed: {
        region() {
            return window.meta.region_configuration.region || 'de';
        },

        marketOverviewOptions() {
            return {
                tabs: [
                    {
                        title: i18next.t('plain_indices'),
                        symbols: [
                            { s: 'FOREXCOM:SPXUSD', d: 'S&P 500' },
                            { s: 'INDEX:NKY', d: 'Nikkei 225' },
                            { s: 'INDEX:DEU40', d: 'DAX Index' },
                            { s: 'FOREXCOM:UKXGBP', d: 'UK 100' },
                            { s: 'BIST:XU100', d: 'BIST 100' },
                            { s: 'CME_MINI:RTY1!', d: 'Russel 2000' },
                            { s: 'CME_MINI:NQ1!', d: 'Nasdaq 100' }
                        ],
                        originalTitle: 'Indices'
                    },
                    {
                        title: i18next.t('plain_commodities'),
                        symbols: [
                            { s: 'COMEX:GC1!', d: 'Gold' },
                            { s: 'COMEX:SI1!', d: 'Silber' },
                            { s: 'COMEX:HG1!', d: 'Kupfer' },
                            { s: 'NYMEX:CL1!', d: 'WTI' },
                            { s: 'NYMEX:PA1!', d: 'Palladium' },
                            { s: 'COMEX:UX1!', d: 'Uranium' },
                            { s: 'CURRENCYCOM:NATURALGAS', d: 'Erdgas' }
                        ],
                        originalTitle: 'Futures'
                    },
                    {
                        title: 'Altcoins',
                        symbols: [
                            { s: 'BINANCE:ADAUSD', d: 'Cardano' },
                            { s: 'COINBASE:CROUSD', d: 'Cronos' },
                            { s: 'BINANCE:SOLUSD', d: 'Solana' },
                            { s: 'BINANCE:MANAUSD', d: 'Decentraland' },
                            { s: 'BINANCE:LINKUSD', d: 'Chainlink' },
                            { s: 'BINANCE:DOTUSD', d: 'Polkadot' },
                            { s: 'BINANCE:MATICUSD', d: 'Polygon' }
                        ],
                        originalTitle: 'Bonds'
                    },
                    {
                        title: 'Forex',
                        symbols: [
                            { s: 'FX:EURUSD', d: 'EUR/USD' },
                            { s: 'FX:GBPUSD', d: 'GBP/USD' },
                            { s: 'FX:USDJPY', d: 'USD/JPY' },
                            { s: 'FX:USDCAD', d: 'USD/CAD' },
                            { s: 'FX:EURTRY', d: 'EUR/TRY' },
                            { s: 'FX:EURCAD', d: 'EUR/CAD' },
                            { s: 'FX:GBPJPY', d: 'GBP/JPY' }
                        ],
                        originalTitle: 'Forex'
                    }
                ],
                showSymbolLogo: true,
                colorTheme: 'light',
                isTransparent: false,
                displayMode: 'adaptive',
                locale: 'en'
            };
        }
    },

    watch: {
        '$route.fullPath': function () {
            this.loading = true;
            this.$nextTick(() => {
                this.loading = false;
            });
        }
    },
    mounted() {
        this.loading = true;

        this.indexContentstreams({ key: 'popular' }).then(() => {
            this.loading = false;
        });
    },

    methods: {
        paginate(page) {
            this.indexContentstreams({
                page,
                key: 'popular'
            });
        },
        async indexContentstreams(params) {
            const config = {
                params: {
                    popular: true,
                    limit: 3
                }
            };

            _.merge(config.params, params);

            return await this.$store.dispatch('posts/index', config);
        }
    }
};
</script>
